import { AxiosResponse } from 'axios';
import api from 'config/api.config';

// ==================== Interfaces ====================

// Dashboard Overview
export interface DashboardOverview {
  revenue: {
    total: number;
    mrr: number;
    arr: number;
    growth: number;
    lastMonth: number;
    currency: string;
  };
  users: {
    total: number;
    active: number;
    trial: number;
    paid: number;
    growth: number;
    newThisMonth: number;
  };
  socialAccounts: {
    total: number;
    active: number;
    instagram: number;
    facebook: number;
    twitter: number;
    growth: number;
  };
  subscriptions: {
    active: number;
    cancelled: number;
    suspended: number;
    trial: number;
    conversionRate: number;
  };
  system: {
    jobsScheduled: number;
    jobsCompleted: number;
    jobsFailed: number;
    successRate: number;
    lastUpdated: string;
  };
}

// Revenue Analytics
export interface RevenueBreakdown {
  bySubscription: Array<{
    name: string;
    revenue: number;
    percentage: number;
    count: number;
  }>;
  byPaymentMethod: Array<{
    method: string;
    revenue: number;
    percentage: number;
    count: number;
  }>;
}

export interface RevenueTimeline {
  date: string;
  revenue: number;
  transactions: number;
  newSubscriptions: number;
  renewals: number;
}

export interface RevenueAnalytics {
  summary: {
    totalRevenue: number;
    averageRevenuePerUser: number;
    highestRevenue: {
      amount: number;
      date: string;
      day: string;
    };
    lowestRevenue: {
      amount: number;
      date: string;
      day: string;
    };
  };
  breakdown: RevenueBreakdown;
  timeline: RevenueTimeline[];
  projections: {
    nextMonth: number;
    nextQuarter: number;
    confidence: number;
  };
}

// User Analytics
export interface UserSegmentation {
  bySubscription: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  byStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  bySignupMethod: Array<{
    method: string;
    count: number;
    percentage: number;
  }>;
}

export interface UserTimeline {
  date: string;
  total: number;
  new: number;
  churned: number;
  netGrowth: number;
  growthRate: number;
}

export interface UserAnalytics {
  overview: {
    total: number;
    active: number;
    inactive: number;
    growth: {
      percentage: number;
      absolute: number;
      trend: string;
    };
  };
  segmentation: UserSegmentation;
  timeline: UserTimeline[];
  cohortAnalysis: {
    week1Retention: number;
    week2Retention: number;
    month1Retention: number;
    month3Retention: number;
  };
  demographics: {
    topCountries: Array<{
      country: string;
      count: number;
      percentage: number;
    }>;
    languages: Array<{
      language: string;
      count: number;
      percentage: number;
    }>;
  };
}

// Social Accounts Analytics
export interface SocialAccountsAnalytics {
  overview: {
    total: number;
    active: number;
    inactive: number;
    locked: number;
    growth: number;
  };
  byPlatform: Array<{
    platform: string;
    total: number;
    active: number;
    averagePerUser: number;
    topUsers?: Array<{
      userId: string;
      username: string;
      accounts: number;
    }>;
  }>;
  activityStats: {
    totalJobsScheduled: number;
    totalActionsPerformed: number;
    averageActionsPerAccount: number;
    mostActiveAccount: {
      username: string;
      platform: string;
      actions: number;
    };
  };
  healthMetrics: {
    accountsWithIssues: number;
    accountsNeedingReauth: number;
    accountsWithFailedJobs: number;
    averageSuccessRate: number;
  };
}

// Subscription Analytics
export interface SubscriptionAnalytics {
  overview: {
    active: number;
    trial: number;
    cancelled: number;
    suspended: number;
    expired: number;
  };
  conversion: {
    trialToStarter: {
      rate: number;
      converted: number;
      total: number;
      averageDaysToConvert: number;
    };
    byMonth: Array<{
      month: string;
      rate: number;
      converted: number;
    }>;
  };
  churn: {
    rate: number;
    thisMonth: number;
    lastMonth: number;
    reasons: Array<{
      reason: string;
      count: number;
    }>;
    recoveryAttempts: {
      sent: number;
      successful: number;
      pending: number;
      failed: number;
    };
  };
  lifetime: {
    averageLTV: number;
    averageSubscriptionLength: number;
    longestSubscriber: {
      userId: string;
      months: number;
      totalPaid: number;
    };
  };
}

// Charts
export interface RevenueChartData {
  period: string;
  granularity: string;
  currency: string;
  data: Array<{
    date: string;
    revenue: number;
    transactions: number;
    newSubscriptions: number;
    renewals: number;
    refunds: number;
    netRevenue: number;
  }>;
  summary: {
    totalRevenue: number;
    averageDaily: number;
    peak: number;
    peakDate: string;
    lowest: number;
    lowestDate: string;
  };
}

export interface UserGrowthChartData {
  period: string;
  data: Array<{
    date: string;
    total: number;
    new: number;
    churned: number;
    netGrowth: number;
    growthRate: number;
  }>;
  summary: {
    startTotal: number;
    endTotal: number;
    totalNew: number;
    totalChurned: number;
    netGrowth: number;
    growthRate: number;
  };
}

// Transactions
export interface Transaction {
  id: string;
  invoiceNumber: string;
  user: {
    id: string;
    name: string | null;
    email: string | null;
  } | null;
  amount: number | null;
  currency: string | null;
  subscriptionType: string | null;
  status: string | null;
  paymentMethod: string | null;
  transactionId: string | null;
  date: string | null;
  type: string | null;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  pagination: {
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  };
  summary: {
    totalAmount: number;
    completedCount: number;
    failedCount: number;
    pendingCount: number;
  };
}

// Top Users
export interface TopUser {
  rank: number;
  user: {
    id: string;
    name: string | null;
    email: string | null;
    avatar?: string | null;
  } | null;
  totalRevenue: number | null;
  subscriptionType: string | null;
  subscriptionLength: number | null;
  socialAccounts: number | null;
  totalActions: number | null;
}

export interface TopUsersResponse {
  metric: string;
  users: TopUser[];
}

// System Health
export interface SystemHealth {
  status: string;
  uptime: number;
  lastRestart: string;
  jobs: {
    scheduled: number;
    running: number;
    completed: number;
    failed: number;
    pending: number;
    successRate: number;
    averageExecutionTime: number;
  };
  database: {
    status: string;
    responseTime: number;
    connections: {
      active: number;
      idle: number;
      total: number;
    };
  };
  queue: {
    redis: {
      status: string;
      queueLength: number;
      processingRate: number;
    };
  };
  api: {
    requestsToday: number;
    averageResponseTime: number;
    errorRate: number;
  };
}

// Failed Payments
export interface FailedPayment {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  amount: number;
  subscriptionType: string;
  failedDate: string;
  reason: string;
  retryCount: number;
  maxRetries: number;
  nextRetry: string;
  status: string;
  remindersSent: number;
}

export interface FailedPaymentsResponse {
  summary: {
    total: number;
    pendingRetry: number;
    maxRetriesReached: number;
    recovered: number;
    potentialRevenueLoss: number;
  };
  payments: FailedPayment[];
}

// Activity Log
export interface Activity {
  id: string;
  timestamp: string;
  type: string;
  action: string;
  user: {
    id: string;
    name: string;
    email?: string;
  };
  details: any;
  ipAddress: string;
  userAgent: string;
}

export interface ActivityLogResponse {
  activities: Activity[];
  pagination: {
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  };
}

// Export
export interface ExportDataPayload {
  type: 'users' | 'payments' | 'social_accounts' | 'all';
  format: 'csv' | 'excel' | 'json';
  filters?: {
    startDate?: string;
    endDate?: string;
    subscriptionType?: string;
    status?: string;
  };
  fields?: string[];
}

export interface ExportDataResponse {
  exportId: string;
  status: string;
  estimatedTime: number;
  downloadUrl: string | null;
  expiresAt: string;
}

// Notifications
export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

export interface NotificationsResponse {
  unread: number;
  notifications: Notification[];
}

// ==================== API Functions ====================

/**
 * 1. Dashboard Overview
 */
export const getDashboardOverview = async (): Promise<DashboardOverview> => {
  try {
    const response: AxiosResponse<DashboardOverview> = await api.get(
      '/api/admin/dashboard/overview'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch dashboard overview');
  }
};

/**
 * 2. Revenue Analytics
 */
export const getRevenueAnalytics = async (
  period: 'day' | 'week' | 'month' | 'year' = 'month',
  startDate?: string,
  endDate?: string,
  groupBy: 'day' | 'week' | 'month' = 'day'
): Promise<RevenueAnalytics> => {
  try {
    const params: any = { period, groupBy };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response: AxiosResponse<RevenueAnalytics> = await api.get(
      '/api/admin/analytics/revenue',
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch revenue analytics');
  }
};

/**
 * 3. User Analytics
 */
export const getUserAnalytics = async (
  period: 'day' | 'week' | 'month' | 'year' = 'month',
  groupBy?: 'day' | 'week' | 'month'
): Promise<UserAnalytics> => {
  try {
    const params: any = { period };
    if (groupBy) params.groupBy = groupBy;

    const response: AxiosResponse<UserAnalytics> = await api.get(
      '/api/admin/analytics/users',
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user analytics');
  }
};

/**
 * 4. Social Accounts Analytics
 */
export const getSocialAccountsAnalytics = async (): Promise<SocialAccountsAnalytics> => {
  try {
    const response: AxiosResponse<SocialAccountsAnalytics> = await api.get(
      '/api/admin/analytics/social-accounts'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch social accounts analytics');
  }
};

/**
 * 5. Subscription Analytics
 */
export const getSubscriptionAnalytics = async (): Promise<SubscriptionAnalytics> => {
  try {
    const response: AxiosResponse<SubscriptionAnalytics> = await api.get(
      '/api/admin/analytics/subscriptions'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription analytics');
  }
};

/**
 * 6. Revenue Timeline Chart
 */
export const getRevenueTimeline = async (
  period: '7d' | '30d' | '90d' | '12m' | 'all' = '30d',
  granularity?: 'day' | 'week' | 'month'
): Promise<RevenueChartData> => {
  try {
    const params: any = { period };
    if (granularity) params.granularity = granularity;

    const response: AxiosResponse<RevenueChartData> = await api.get(
      '/api/admin/charts/revenue-timeline',
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch revenue timeline');
  }
};

/**
 * 7. User Growth Chart
 */
export const getUserGrowthChart = async (
  period: '7d' | '30d' | '90d' | '12m' | 'all' = '30d'
): Promise<UserGrowthChartData> => {
  try {
    const response: AxiosResponse<UserGrowthChartData> = await api.get(
      '/api/admin/charts/user-growth',
      { params: { period } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user growth chart');
  }
};

/**
 * 8. Recent Transactions
 */
export const getRecentTransactions = async (
  limit: number = 20,
  offset: number = 0,
  status: 'all' | 'completed' | 'failed' | 'cancelled' | 'pending' = 'all'
): Promise<TransactionsResponse> => {
  try {
    const response: AxiosResponse<TransactionsResponse> = await api.get(
      '/api/admin/transactions/recent',
      { params: { limit, offset, status } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch recent transactions');
  }
};

/**
 * 9. Top Performing Users
 */
export const getTopUsers = async (
  metric: 'revenue' | 'social_accounts' | 'actions' | 'engagement' = 'revenue',
  limit: number = 10
): Promise<TopUsersResponse> => {
  try {
    const response: AxiosResponse<TopUsersResponse> = await api.get(
      '/api/admin/analytics/top-users',
      { params: { metric, limit } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch top users');
  }
};

/**
 * 10. System Health
 */
export const getSystemHealth = async (): Promise<SystemHealth> => {
  try {
    const response: AxiosResponse<SystemHealth> = await api.get(
      '/api/admin/system/health'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch system health');
  }
};

/**
 * 11. Failed Payments
 */
export const getFailedPayments = async (
  status: 'all' | 'pending_retry' | 'max_retries' | 'recovered' = 'all'
): Promise<FailedPaymentsResponse> => {
  try {
    const response: AxiosResponse<FailedPaymentsResponse> = await api.get(
      '/api/admin/payments/failed',
      { params: { status } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch failed payments');
  }
};

/**
 * 12. Activity Log
 */
export const getActivityLog = async (
  limit: number = 50,
  type: 'all' | 'user' | 'payment' | 'social' | 'system' = 'all'
): Promise<ActivityLogResponse> => {
  try {
    const response: AxiosResponse<ActivityLogResponse> = await api.get(
      '/api/admin/activity/recent',
      { params: { limit, type } }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch activity log');
  }
};

/**
 * 13. Export Data
 */
export const exportData = async (payload: ExportDataPayload): Promise<ExportDataResponse> => {
  try {
    const response: AxiosResponse<ExportDataResponse> = await api.post(
      '/api/admin/export/data',
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to export data');
  }
};

/**
 * Get Export Status
 */
export const getExportStatus = async (exportId: string): Promise<ExportDataResponse> => {
  try {
    const response: AxiosResponse<ExportDataResponse> = await api.get(
      `/api/admin/export/status/${exportId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get export status');
  }
};

/**
 * 14. Notifications
 */
export const getNotifications = async (): Promise<NotificationsResponse> => {
  try {
    const response: AxiosResponse<NotificationsResponse> = await api.get(
      '/api/admin/notifications'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch notifications');
  }
};

/**
 * Mark Notification as Read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    await api.patch(
      `/api/admin/notifications/${notificationId}/read`,
      {}
    );
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to mark notification as read');
  }
};
